import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import GuideButton from '../../common/guide/GuideButton';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TodayIcon from '@mui/icons-material/Today';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from '../../reports/hub/useReportsHubBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { salesApi, getirApi, migrosApi, yemeksepetiApi, trendyolApi, mainApi } from '../../../config/apiClient';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../../accounting/shared/StatsCard';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import UnifiedOrderDetailModal from './modals/UnifiedOrderDetailModal';
import FoodProductMappingModal from './modals/FoodProductMappingModal';
import BlacklistModal from './modals/BlacklistModal';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import { AppDatePicker } from '../../common/AppDatePicker';

type OrderPlatform = 'qr' | 'kiosk' | 'ecommerce' | 'getir' | 'migros' | 'yemeksepeti' | 'trendyol' | 'phone';

type OrderStatus = 'new' | 'accepted' | 'preparing' | 'onway' | 'delivered' | 'cancelled' | 'completed' | 'unknown';

interface UnifiedOrder {
  id: string;
  platform: OrderPlatform;
  branchId?: string;
  branchName?: string;
  customerName: string;
  shortOrderNo: string;
  itemCount: number;
  totalAmount: number;
  createdAtUtc: string;
  status: OrderStatus;
  paymentMethod?: string;
  isPaid?: boolean;
  courierName?: string;
}

interface Branch {
  id: string;
  name: string;
}

const PLATFORM_COLORS: Record<OrderPlatform, string> = {
  trendyol: '#06C167',
  yemeksepeti: '#e31e24',
  getir: '#5D3EBC',
  migros: '#FF6000',
  qr: '#2e7d32',
  kiosk: '#0D47A1',
  ecommerce: '#00796B',
  phone: '#4CAF50',
};

const PLATFORM_LABELS: Record<OrderPlatform, string> = {
  trendyol: 'Uber Eats',
  yemeksepeti: 'Yemeksepeti',
  getir: 'Getir',
  migros: 'Migros',
  qr: 'QR Sipariş',
  kiosk: 'Kiosk',
  ecommerce: 'E-Ticaret',
  phone: 'Telefon',
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  new: '#FFC107',
  accepted: '#2196F3',
  preparing: '#FF9800',
  onway: '#9C27B0',
  delivered: '#4CAF50',
  completed: '#4CAF50',
  cancelled: '#F44336',
  unknown: '#9E9E9E',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Yeni',
  accepted: 'Kabul Edildi',
  preparing: 'Hazırlanıyor',
  onway: 'Yolda',
  delivered: 'Teslim Edildi',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
  unknown: 'Bilinmiyor',
};

const normalizeGetirStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toString();
  if (['350', '400'].includes(s)) return 'new';
  if (['500'].includes(s)) return 'accepted';
  if (['550'].includes(s)) return 'preparing';
  if (['600', '700', '800'].includes(s)) return 'onway';
  if (['900'].includes(s)) return 'delivered';
  if (['1500', '1600'].includes(s)) return 'cancelled';
  return 'unknown';
};

const normalizeMigrosStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toLowerCase();
  if (s === 'new' || s === 'pending') return 'new';
  if (s === 'accepted' || s === 'approved') return 'accepted';
  if (s === 'preparing') return 'preparing';
  if (s === 'on_delivery' || s === 'on_way') return 'onway';
  if (s === 'delivered' || s === 'completed') return 'delivered';
  if (s === 'cancelled' || s === 'rejected') return 'cancelled';
  return 'unknown';
};

const normalizeYsStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toUpperCase();
  if (s === 'PENDING' || s === 'NEW') return 'new';
  if (s === 'APPROVED' || s === 'ACCEPTED') return 'accepted';
  if (s === 'PREPARING') return 'preparing';
  if (s === 'PICKED_UP' || s === 'ON_DELIVERY' || s === 'ON_WAY') return 'onway';
  if (s === 'DELIVERED' || s === 'COMPLETED') return 'delivered';
  if (s === 'REJECTED' || s === 'CANCELLED' || s === 'EXPIRED') return 'cancelled';
  return 'unknown';
};

const normalizeTrendyolStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toLowerCase();
  if (s === 'created' || s === 'pending') return 'new';
  if (s === 'picking' || s === 'invoiced') return 'accepted';
  if (s === 'preparing') return 'preparing';
  if (s === 'shipped' || s === 'unsupplied') return 'onway';
  if (s === 'delivered') return 'delivered';
  if (s === 'cancelled') return 'cancelled';
  return 'unknown';
};

/** API'lerden gelen odeme bilgisini kisa etikete cevirir */
const formatPaymentLabel = (paymentMethod?: string | null): string => {
  if (!paymentMethod || typeof paymentMethod !== 'string') return '';
  const s = paymentMethod.toLowerCase().trim();
  if (!s) return '';
  if (s.includes('online') || s.includes('pay_with_card') || s === 'online' || s === 'credit_card') return 'Online';
  if (s.includes('nakit') || s.includes('cash') || s === 'cash') return 'Kapida Nakit';
  if (s.includes('kart') || s.includes('card') || s === 'pos') return 'Kapida Kart';
  if (s.includes('pay_with_on_delivery')) return 'Kapida';
  if (s === 'platform') return 'Platform';
  return paymentMethod;
};

const normalizeMasaSiparisiStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toLowerCase();
  if (s === 'bekliyor' || s === 'yeni' || s === 'pending' || s === 'new') return 'new';
  if (s === 'onaylandi' || s === 'kabul edildi' || s === 'accepted') return 'accepted';
  if (s === 'hazirlaniyor' || s === 'preparing') return 'preparing';
  if (s === 'hazir' || s === 'ready') return 'onway';
  if (s === 'teslim edildi' || s === 'delivered' || s === 'tamamlandi' || s === 'completed') return 'delivered';
  if (s === 'iptal' || s === 'cancelled') return 'cancelled';
  return 'unknown';
};

const determineMasaSiparisiPlatform = (syncSource?: string | null, siparisId?: string | null): OrderPlatform => {
  if (syncSource) {
    const s = syncSource.toLowerCase();
    if (s.includes('kiosk')) return 'kiosk';
    if (s.includes('ecommerce') || s.includes('e-commerce') || s.includes('ecom')) return 'ecommerce';
    if (s.includes('qr') || s.includes('qrmenu')) return 'qr';
  }
  if (siparisId) {
    if (siparisId.startsWith('D-') || siparisId.startsWith('P-')) return 'kiosk';
    const parts = siparisId.split('-');
    if (parts.length >= 2) {
      const sourceType = parts[parts.length - 2].toLowerCase();
      if (sourceType === 'qr') return 'qr';
      if (sourceType === 'kiosk') return 'kiosk';
      if (sourceType === 'ecom' || sourceType === 'ecommerce') return 'ecommerce';
    }
  }
  return 'qr';
};

const normalizePackageStatus = (status?: string | null): OrderStatus => {
  if (!status) return 'unknown';
  const s = status.toUpperCase();
  if (s === 'PENDING' || s === 'NEW') return 'new';
  if (s === 'CONFIRMED' || s === 'ACCEPTED') return 'accepted';
  if (s === 'PREPARING') return 'preparing';
  if (s === 'ON_WAY' || s === 'SHIPPED' || s === 'PICKED_UP') return 'onway';
  if (s === 'DELIVERED' || s === 'COMPLETED') return 'delivered';
  if (s === 'CANCELLED') return 'cancelled';
  return 'unknown';
};

const ensureUtcSuffix = (dateStr: string): string => {
  if (!dateStr) return '';
  if (dateStr.endsWith('Z') || /[+-]\d{2}(:\d{2})?$/.test(dateStr)) return dateStr;
  return dateStr + 'Z';
};

const determinePlatform = (syncSource?: string | null): OrderPlatform => {
  if (!syncSource) return 'phone';
  const s = syncSource.toLowerCase();
  if (s.includes('getir')) return 'getir';
  if (s.includes('migros')) return 'migros';
  if (s.includes('yemeksepeti') || s.includes('ys')) return 'yemeksepeti';
  if (s.includes('trendyol')) return 'trendyol';
  if (s.includes('kiosk')) return 'kiosk';
  if (s.includes('ecommerce') || s.includes('e-commerce')) return 'ecommerce';
  if (s.includes('qr') || s.includes('menu') || s.includes('quicksale') || s.includes('restaurant')) return 'qr';
  return 'phone';
};

interface AllOrdersPageProps {
  /** Yönetici paneli: şube filtresi bu şubeye kilitlenir */
  lockedBranchId?: string | null;
  /** Gömülü modda geri butonu route'a dönmek yerine bunu çağırır */
  onBack?: () => void;
}

const AllOrdersPage: React.FC<AllOrdersPageProps> = ({ lockedBranchId = null, onBack }) => {
  const navigate = useNavigate();
  const { goBack } = useReportsHubBack('/tum-siparisler', () => (onBack ? onBack() : navigate('/siparis-yonetimi')));
  const { userDetails, branches, loadBranches } = useAuth();
  const companyId = userDetails?.companyId;
  const branchesLoadedRef = useRef(false);

  useEffect(() => {
    if (companyId && !branchesLoadedRef.current) {
      branchesLoadedRef.current = true;
      loadBranches();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  // Platform ürün eşleştirme — şirket görünümü (bütün şubeler)
  const [productMappingOpen, setProductMappingOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCounts, setTotalCounts] = useState({
    package: 0, getir: 0, migros: 0, yemeksepeti: 0, trendyol: 0, masaSiparisi: 0,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState<string>(lockedBranchId || '');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<UnifiedOrder | null>(null);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState<string | number | null>(null);
  const [detailPlatform, setDetailPlatform] = useState<OrderPlatform | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  const branchList = useMemo<Branch[]>(() => {
    if (!branches || !Array.isArray(branches)) return [];
    return branches.filter(b => b.id !== 'null');
  }, [branches]);

  const getBranchName = useCallback((branchId?: string) => {
    if (!branchId || !branches || !Array.isArray(branches)) return '-';
    const b = branches.find(br => br.id === branchId);
    return b?.name || '-';
  }, [branches]);

  const totalCount = useMemo(() => {
    return totalCounts.package + totalCounts.getir + totalCounts.migros + totalCounts.yemeksepeti + totalCounts.trendyol + totalCounts.masaSiparisi;
  }, [totalCounts]);

  const cancelledCount = useMemo(() => {
    return orders.filter(o => o.status === 'cancelled').length;
  }, [orders]);

  const totalBalance = useMemo(() => {
    return orders.reduce((sum, o) => sum + o.totalAmount, 0);
  }, [orders]);

  const hasDateFilter = startDate !== '' || endDate !== '';

  const fetchAllOrders = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    const apiPage = page + 1;
    const perApi = pageSize;
    const search = debouncedSearch || undefined;

    const buildParams = (extra?: Record<string, string>) => {
      const p = new URLSearchParams();
      p.append('page', apiPage.toString());
      p.append('pageSize', perApi.toString());
      if (search) p.append('search', search);
      if (branchFilter) p.append('branchId', branchFilter);
      if (startDate) p.append('startDate', startDate);
      if (endDate) p.append('endDate', endDate);
      if (extra) Object.entries(extra).forEach(([k, v]) => p.append(k, v));
      return p.toString();
    };

    const buildSalesParams = () => {
      const p = new URLSearchParams();
      p.append('page', apiPage.toString());
      p.append('pageSize', perApi.toString());
      if (startDate) p.append('startDate', startDate);
      if (endDate) p.append('endDate', endDate);
      if (search) p.append('search', search);
      if (branchFilter) p.append('branchId', branchFilter);
      return p.toString();
    };

    const isPlatformIncluded = (p: string) => !platformFilter || platformFilter === p;

    const buildMasaSiparisiParams = () => {
      const p = new URLSearchParams();
      p.append('page', apiPage.toString());
      p.append('pageSize', perApi.toString());
      if (startDate) p.append('startDate', startDate);
      if (endDate) p.append('endDate', endDate);
      if (search) p.append('search', search);
      if (branchFilter) p.append('branchId', branchFilter);
      return p.toString();
    };

    const fetchers = [
      (isPlatformIncluded('package') || isPlatformIncluded('phone') || isPlatformIncluded('qr') || isPlatformIncluded('kiosk') || isPlatformIncluded('ecommerce') || !platformFilter)
        ? salesApi.get(`/api/PackageOrder/by-date-range?${buildSalesParams()}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),

      isPlatformIncluded('getir')
        ? getirApi.get(`/api/getir-orders/company/${encodeURIComponent(companyId)}/list?${buildParams()}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),

      isPlatformIncluded('migros')
        ? migrosApi.get(`/api/migros-orders/list?${buildParams()}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),

      isPlatformIncluded('yemeksepeti')
        ? yemeksepetiApi.get(`/api/ys-orders/list?${buildParams()}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),

      isPlatformIncluded('trendyol')
        ? trendyolApi.get(`/api/trendyol/order/list?${buildParams({ companyId })}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),

      (isPlatformIncluded('qr') || isPlatformIncluded('kiosk') || isPlatformIncluded('ecommerce') || !platformFilter)
        ? mainApi.get(`/api/MasaSiparisi/orders?${buildMasaSiparisiParams()}`)
            .then(r => r.data).catch(() => null)
        : Promise.resolve(null),
    ];

    try {
      const [pkgRes, getirRes, migrosRes, ysRes, trendyolRes, masaSiparisiRes] = await Promise.all(fetchers);

      const newCounts = { package: 0, getir: 0, migros: 0, yemeksepeti: 0, trendyol: 0, masaSiparisi: 0 };
      const merged: UnifiedOrder[] = [];

      if (pkgRes?.Success || pkgRes?.success) {
        const data = pkgRes.Data || pkgRes.data || [];
        newCounts.package = pkgRes.TotalCount ?? pkgRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          const platform = determinePlatform(o.syncSource ?? o.SyncSource);
          if (platformFilter && platform !== platformFilter) return;
          merged.push({
            id: `pkg_${o.id ?? o.Id}`,
            platform,
            branchId: o.branchId ?? o.BranchId,
            branchName: getBranchName(o.branchId ?? o.BranchId),
            customerName: o.accountName ?? o.AccountName ?? '-',
            shortOrderNo: o.shortCode ?? o.ShortCode ?? o.orderNumber ?? o.OrderNumber ?? '-',
            itemCount: (o.items ?? o.Items ?? []).reduce((sum: number, i: any) => sum + (i.quantity ?? i.Quantity ?? 1), 0),
            totalAmount: o.totalAmount ?? o.TotalAmount ?? 0,
            createdAtUtc: o.createdAt ?? o.CreatedAt ?? '',
            status: normalizePackageStatus(o.orderStatus ?? o.OrderStatus),
            paymentMethod: o.paymentMethod ?? o.PaymentMethod,
            isPaid: (o.paymentStatus ?? o.PaymentStatus) === 'PAID',
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      if (getirRes?.Success || getirRes?.success) {
        const data = getirRes.Data || getirRes.data || [];
        newCounts.getir = getirRes.TotalCount ?? getirRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          merged.push({
            id: `getir_${o.id ?? o.Id}`,
            platform: 'getir',
            branchId: o.branchId ?? o.BranchId,
            branchName: getBranchName(o.branchId ?? o.BranchId),
            customerName: o.clientName ?? o.ClientName ?? '-',
            shortOrderNo: o.confirmationId ?? o.ConfirmationId ?? '-',
            itemCount: (o.items ?? o.Items ?? []).length,
            totalAmount: Number(o.totalPrice ?? o.TotalPrice ?? 0),
            createdAtUtc: o.createdAt ?? o.CreatedAt ?? '',
            status: normalizeGetirStatus(o.status ?? o.Status),
            paymentMethod: o.paymentMethodText ?? o.PaymentMethodText,
            isPaid: (o.paymentMethod ?? o.PaymentMethod) === 1 || (o.paymentStatus ?? o.PaymentStatus) === 'PAID',
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      if (migrosRes?.Success || migrosRes?.success) {
        const data = migrosRes.Data || migrosRes.data || [];
        newCounts.migros = migrosRes.TotalCount ?? migrosRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          const totalPrice = (o.totalPrice ?? o.TotalPrice ?? 0);
          merged.push({
            id: `migros_${o.id ?? o.Id}`,
            platform: 'migros',
            branchId: o.branchId ?? o.BranchId,
            branchName: getBranchName(o.branchId ?? o.BranchId),
            customerName: o.customerFullName ?? o.CustomerFullName ?? '-',
            shortOrderNo: o.shortCode ?? o.ShortCode ?? String(o.migrosOrderId ?? o.MigrosOrderId ?? '-'),
            itemCount: (o.items ?? o.Items ?? []).length,
            totalAmount: typeof totalPrice === 'number' && totalPrice > 1000 ? totalPrice / 100 : totalPrice,
            createdAtUtc: ensureUtcSuffix(o.createdAt ?? o.CreatedAt ?? ''),
            status: normalizeMigrosStatus(o.status ?? o.Status),
            paymentMethod: o.paymentType ?? o.PaymentType,
            isPaid: !!(o.isOnlinePayment ?? o.IsOnlinePayment) || (o.paymentStatus ?? o.PaymentStatus) === 'PAID',
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      if (ysRes?.Success || ysRes?.success) {
        const data = ysRes.Data || ysRes.data || [];
        newCounts.yemeksepeti = ysRes.TotalCount ?? ysRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          merged.push({
            id: `ys_${o.id ?? o.Id}`,
            platform: 'yemeksepeti',
            branchId: o.branchId ?? o.BranchId,
            branchName: getBranchName(o.branchId ?? o.BranchId),
            customerName: o.customerFullName ?? o.CustomerFullName ?? '-',
            shortOrderNo: o.shortCode ?? o.ShortCode ?? o.code ?? o.Code ?? '-',
            itemCount: (o.items ?? o.Items ?? []).length,
            totalAmount: Number(o.grandTotal ?? o.GrandTotal ?? 0),
            createdAtUtc: ensureUtcSuffix(o.receivedAt ?? o.ReceivedAt ?? o.createdAt ?? o.CreatedAt ?? ''),
            status: normalizeYsStatus(o.status ?? o.Status),
            paymentMethod: o.paymentType ?? o.PaymentType,
            isPaid: (o.paymentStatus ?? o.PaymentStatus) === 'PAID',
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      if (trendyolRes?.Success || trendyolRes?.success) {
        const data = trendyolRes.Data || trendyolRes.data || [];
        newCounts.trendyol = trendyolRes.TotalCount ?? trendyolRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          merged.push({
            id: `trendyol_${o.id ?? o.Id}`,
            platform: 'trendyol',
            branchId: o.branchId ?? o.BranchId,
            branchName: getBranchName(o.branchId ?? o.BranchId),
            customerName: o.customerName ?? o.CustomerName ?? '-',
            shortOrderNo: o.orderCode ?? o.OrderCode ?? o.orderNumber ?? o.OrderNumber ?? '-',
            itemCount: 1,
            totalAmount: Number(o.totalAmount ?? o.TotalAmount ?? 0),
            createdAtUtc: ensureUtcSuffix(o.createdAt ?? o.CreatedAt ?? ''),
            status: normalizeTrendyolStatus(o.orderStatus ?? o.OrderStatus),
            paymentMethod: o.paymentMethod ?? o.PaymentMethod,
            isPaid: !!(o.isPaid ?? o.IsPaid) || (o.paymentMethod ?? o.PaymentMethod) !== 'PAY_WITH_ON_DELIVERY',
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      if (masaSiparisiRes?.success) {
        const data = masaSiparisiRes.data || [];
        newCounts.masaSiparisi = masaSiparisiRes.totalCount ?? data.length;
        data.forEach((o: any) => {
          const siparisKaynagi = o.SyncSource ?? o.syncSource ?? o.SiparisKaynagi ?? o.siparisKaynagi;
          const siparisId = o.SiparisId ?? o.siparisId;
          if (siparisId?.includes('-HS-')) return;
          const platform = determineMasaSiparisiPlatform(siparisKaynagi, siparisId);
          if (platformFilter && platform !== platformFilter) return;

          let itemCount = 0;
          try {
            const detayStr = o.SiparisDetaylari ?? o.siparisDetaylari;
            if (detayStr) {
              const parsed = typeof detayStr === 'string' ? JSON.parse(detayStr) : detayStr;
              const items = parsed.siparisDetaylari || parsed;
              if (Array.isArray(items)) {
                itemCount = items.reduce((sum: number, it: any) => sum + (it.adet || it.quantity || 1), 0);
              }
            }
          } catch { /* ignore parse error */ }

          merged.push({
            id: `masa_${o.Id ?? o.id}`,
            platform,
            branchId: o.BranchId ?? o.branchId,
            branchName: getBranchName(o.BranchId ?? o.branchId),
            customerName: o.KullaniciAdi ?? o.kullaniciAdi ?? '-',
            shortOrderNo: siparisId ?? '-',
            itemCount,
            totalAmount: Number(o.ToplamTutar ?? o.toplamTutar ?? 0),
            createdAtUtc: o.SiparisOlusturmaTarihi ?? o.siparisOlusturmaTarihi ?? '',
            status: normalizeMasaSiparisiStatus(o.SiparisDurumu ?? o.siparisDurumu),
            paymentMethod: o.OdemeYontemi ?? o.odemeYontemi,
            isPaid: (o.OdemeDurumu ?? o.odemeDurumu) === 'Odendi' || o.odemeBasarili === true,
            courierName: o.courierName ?? o.CourierName ?? undefined,
          });
        });
      }

      merged.sort((a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime());
      setOrders(merged);
      setTotalCounts(newCounts);
    } catch (err: any) {
      setError(err.message || 'Siparişler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [companyId, page, pageSize, debouncedSearch, branchFilter, startDate, endDate, platformFilter, getBranchName]);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders]);

  const columns: DataTableColumn<UnifiedOrder>[] = useMemo(() => [
    {
      id: 'rowNo',
      label: '#',
      width: '50px',
      align: 'center' as const,
      render: (_row: UnifiedOrder, index: number) => page * pageSize + index + 1,
    },
    {
      id: 'platform',
      label: 'Platform',
      width: '130px',
      render: (row: UnifiedOrder) => (
        <Chip
          label={PLATFORM_LABELS[row.platform]}
          size="small"
          sx={{
            bgcolor: PLATFORM_COLORS[row.platform],
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      ),
    },
    {
      id: 'branch',
      label: 'Şube',
      width: '120px',
      render: (row: UnifiedOrder) => row.branchName || '-',
    },
    {
      id: 'customerName',
      label: 'Müşteri Adı',
      width: '150px',
      render: (row: UnifiedOrder) => row.customerName,
    },
    {
      id: 'orderNo',
      label: 'Sipariş No',
      width: '120px',
      render: (row: UnifiedOrder) => row.shortOrderNo,
    },
    {
      id: 'itemCount',
      label: 'Ürün Adeti',
      width: '80px',
      align: 'right' as const,
      render: (row: UnifiedOrder) => row.itemCount,
    },
    {
      id: 'totalAmount',
      label: 'Tutar',
      width: '100px',
      align: 'right' as const,
      render: (row: UnifiedOrder) => (
        <Typography fontWeight="bold" fontSize="0.875rem">
          {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.totalAmount)} ₺
        </Typography>
      ),
    },
    {
      id: 'orderDate',
      label: 'Tarih',
      width: '130px',
      render: (row: UnifiedOrder) => {
        if (!row.createdAtUtc) return '-';
        try {
          return new Date(row.createdAtUtc).toLocaleString('tr-TR', {
            timeZone: 'Europe/Istanbul',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return row.createdAtUtc;
        }
      },
    },
    {
      id: 'status',
      label: 'Durum',
      width: '110px',
      render: (row: UnifiedOrder) => (
        <Chip
          label={STATUS_LABELS[row.status]}
          size="small"
          sx={{
            bgcolor: STATUS_COLORS[row.status] + '20',
            color: STATUS_COLORS[row.status],
            fontWeight: 600,
            fontSize: '0.75rem',
            border: `1px solid ${STATUS_COLORS[row.status]}40`,
          }}
        />
      ),
    },
    {
      id: 'paymentInfo',
      label: 'Ö. bilgisi',
      width: '100px',
      render: (row: UnifiedOrder) => (
        <Typography variant="body2" fontSize="0.8rem">
          {formatPaymentLabel(row.paymentMethod) || '-'}
        </Typography>
      ),
    },
    {
      id: 'paymentStatus',
      label: 'Ö. Durumu',
      width: '90px',
      align: 'center' as const,
      render: (row: UnifiedOrder) => (
        <Chip
          label={row.isPaid ? '✓' : '₺'}
          size="small"
          color={row.isPaid ? 'success' : 'warning'}
          variant={row.isPaid ? 'filled' : 'outlined'}
          sx={{ minWidth: 32, fontWeight: 700 }}
        />
      ),
    },
    {
      id: 'courierName',
      label: 'Kurye',
      width: '120px',
      render: (row: UnifiedOrder) => (
        <Typography variant="body2" fontSize="0.8rem" noWrap>
          {row.courierName || '-'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'İşlemler',
      width: '60px',
      align: 'center' as const,
      render: (row: UnifiedOrder) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedOrder(row);
            setActionMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [page, pageSize]);

  const handleExportExcel = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Tüm Siparişler');

    sheet.columns = [
      { header: '#', key: 'rowNo', width: 8 },
      { header: 'Platform', key: 'platform', width: 15 },
      { header: 'Şube', key: 'branch', width: 20 },
      { header: 'Müşteri Adı', key: 'customerName', width: 25 },
      { header: 'Sipariş No', key: 'orderNo', width: 18 },
      { header: 'Ürün Adeti', key: 'itemCount', width: 12 },
      { header: 'Tutar', key: 'totalAmount', width: 15 },
      { header: 'Tarih', key: 'orderDate', width: 20 },
      { header: 'Durum', key: 'status', width: 15 },
      { header: 'Ö. bilgisi', key: 'paymentInfo', width: 14 },
      { header: 'Ö. Durumu', key: 'paymentStatus', width: 12 },
      { header: 'Kurye', key: 'courierName', width: 20 },
    ];

    orders.forEach((o, i) => {
      sheet.addRow({
        rowNo: i + 1,
        platform: PLATFORM_LABELS[o.platform],
        branch: o.branchName || '-',
        customerName: o.customerName,
        orderNo: o.shortOrderNo,
        itemCount: o.itemCount,
        totalAmount: o.totalAmount,
        orderDate: o.createdAtUtc ? new Date(o.createdAtUtc).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '-',
        status: STATUS_LABELS[o.status],
        paymentInfo: formatPaymentLabel(o.paymentMethod) || '-',
        paymentStatus: o.isPaid ? 'Ödendi' : 'Bekliyor',
        courierName: o.courierName || '-',
      });
    });

    const headerRow = sheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const dateSuffix = startDate && endDate ? `${startDate}_${endDate}` : 'tum';
    saveAs(new Blob([buffer]), `tum_siparisler_${dateSuffix}.xlsx`);
  }, [orders, startDate, endDate]);

  const handleClearFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setBranchFilter(lockedBranchId || '');
    setPlatformFilter('');
    setSearchTerm('');
    setPage(0);
  }, [lockedBranchId]);

  const parseRawId = (prefixedId: string): string => {
    return prefixedId.replace(/^(pkg_|getir_|migros_|ys_|trendyol_|masa_)/, '');
  };

  const handleOpenDetail = useCallback((order: UnifiedOrder) => {
    setDetailOrderId(parseRawId(order.id));
    setDetailPlatform(order.platform);
    setDetailModalOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailModalOpen(false);
    setDetailOrderId(null);
    setDetailPlatform(null);
  }, []);

  const platformOptions = useMemo(() => [
    { id: '', label: 'Tüm Platformlar' },
    { id: 'phone', label: 'Telefon' },
    { id: 'qr', label: 'QR Sipariş' },
    { id: 'kiosk', label: 'Kiosk' },
    { id: 'ecommerce', label: 'E-Ticaret' },
    { id: 'getir', label: 'Getir' },
    { id: 'migros', label: 'Migros' },
    { id: 'yemeksepeti', label: 'Yemeksepeti' },
    { id: 'trendyol', label: 'Uber Eats' },
  ], []);

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={goBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            Tüm Siparişler
          </Typography>
          <GuideButton guideKey="sales.allOrders" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<LinkIcon />} onClick={() => setProductMappingOpen(true)}>
            Platform Ürün Eşleştirme
          </Button>
          <Button variant="outlined" size="small" startIcon={<BlockIcon />} onClick={() => setBlacklistOpen(true)}
            sx={{ color: 'text.primary', borderColor: 'text.primary' }}>
            Kara Liste
          </Button>
          <IconButton onClick={handleExportExcel} color="primary" title="Excel İndir">
            <FileDownloadIcon />
          </IconButton>
        </Box>
      </Box>

      <FoodProductMappingModal
        open={productMappingOpen}
        onClose={() => setProductMappingOpen(false)}
        branchId={null}
        branches={branchList.map(b => ({ id: b.id, name: b.name }))}
      />

      {/* Kara liste — şirket görünümü (şube seçilerek o şubenin gözünden bakılabilir) */}
      <BlacklistModal
        open={blacklistOpen}
        onClose={() => setBlacklistOpen(false)}
        branchId={null}
        branches={branchList.map(b => ({ id: b.id, name: b.name }))}
      />

      {/* Stats Cards */}
      <StatsCardsContainer loading={loading} cardCount={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<ShoppingBagIcon />}
            color={STATS_COLORS.blue}
            title={totalCount.toLocaleString('tr-TR')}
            subtitle="Toplam Sipariş"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TodayIcon />}
            color={STATS_COLORS.green}
            title={orders.length.toLocaleString('tr-TR')}
            subtitle="Sayfadaki Sipariş"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CancelIcon />}
            color={STATS_COLORS.red}
            title={cancelledCount.toLocaleString('tr-TR')}
            subtitle="İptal Siparişler"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AttachMoneyIcon />}
            color={STATS_COLORS.orange}
            title={formatStatsCurrency(totalBalance)}
            subtitle="Sayfa Toplam Tutar"
          />
        </Grid>
      </StatsCardsContainer>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Ara... (müşteri, sipariş no)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          size="small"
          options={branchList}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={branchList.find(b => b.id === branchFilter) || null}
          onChange={(_, val) => { if (lockedBranchId) return; setBranchFilter(val?.id || ''); setPage(0); }}
          disabled={!!lockedBranchId}
          disableClearable={!!lockedBranchId}
          renderInput={(params) => <TextField {...params} placeholder="Şube" />}
          sx={{ minWidth: 180 }}
        />
        <Autocomplete
          size="small"
          options={platformOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={platformOptions.find(p => p.id === platformFilter) || platformOptions[0]}
          onChange={(_, val) => { setPlatformFilter(val?.id || ''); setPage(0); }}
          renderInput={(params) => <TextField {...params} placeholder="Platform" />}
          sx={{ minWidth: 160 }}
          disableClearable
        />
        <AppDatePicker
          size="small"
          label="Başlangıç"
          value={startDate}
          onChange={(value) => { setStartDate(value); setPage(0); }}
          sx={{ width: 160 }}
        />
        <AppDatePicker
          size="small"
          label="Bitiş"
          value={endDate}
          onChange={(value) => { setEndDate(value); setPage(0); }}
          sx={{ width: 160 }}
        />
        {(hasDateFilter || (branchFilter && !lockedBranchId) || platformFilter || searchTerm) && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<FilterAltOffIcon />}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Temizle
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Data Table */}
      <AccountingDataTable
        columns={columns}
        rows={orders}
        getRowId={(row) => row.id}
        loading={loading}
        serverSidePagination={true}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
        emptyMessage="Sipariş bulunamadı"
        pagination={true}
        onRowClick={handleOpenDetail}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => { setActionMenuAnchor(null); setSelectedOrder(null); }}
      >
        <MenuItem onClick={() => {
          if (selectedOrder) {
            handleOpenDetail(selectedOrder);
          }
          setActionMenuAnchor(null);
          setSelectedOrder(null);
        }}>
          Detaylar
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedOrder) {
            navigate('/siparis-yonetimi');
          }
          setActionMenuAnchor(null);
          setSelectedOrder(null);
        }}>
          Sipariş Yönetimine Git
        </MenuItem>
      </Menu>

      {/* Unified Order Detail Modal */}
      {companyId && (
        <UnifiedOrderDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetail}
          orderId={detailOrderId}
          platform={detailPlatform}
          companyId={companyId}
        />
      )}
    </Box>
  );
};

export default AllOrdersPage;
